import { isArray } from "lodash";
import { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import {
  useCustomQueryParams,
  useDynamicTablesCleanup,
  usePageContext,
  useViewActions,
} from "@ryde/redux";
import {
  activeViewIdAtomFamily,
  companyIdSelector,
  hasKeys,
  Log,
  NO_GROUPING_QS_VALUE,
  tableColumnsOrderStateAF,
  tableColumnsSizingAF,
  tableColumnsSortingAF,
  tableColumnsVisibilityAF,
  tableFiltersSchemaAtomFamily,
  tableGroupingAF,
  updateFinalSchemaLocalStore,
  viewChangesApplyingAtom,
  type IDropdownItem,
} from "@ryde/shared";

import { ViewContext } from "../view.context";

export const useViewDropdown = () => {
  const { entity } = usePageContext();
  const { t } = useTranslation();
  const companyId = useRecoilValue(companyIdSelector);
  const {
    isDropdownOpen: isOpen,
    setIsDropdownOpen: setIsOpen,
    openEditView,
  } = useContext(ViewContext);
  const setIsUpdating = useSetRecoilState(viewChangesApplyingAtom);
  const { resetPageOnFiltersChange } = useDynamicTablesCleanup({ entity });

  const { deleteViewHandler, updateViewCriteriaOnlyHandler, views } =
    useViewActions();
  const [activeViewId, setActiveViewId] = useRecoilState(
    activeViewIdAtomFamily(entity),
  );

  const setFiltersSchema = useSetRecoilState(
    tableFiltersSchemaAtomFamily(entity),
  );

  const setColumnSorting = useSetRecoilState(tableColumnsSortingAF(entity));
  const setColumnGrouping = useSetRecoilState(tableGroupingAF(entity));

  const setColumnOrder = useSetRecoilState(tableColumnsOrderStateAF(entity));

  const setColumnVisibility = useSetRecoilState(
    tableColumnsVisibilityAF(entity),
  );
  const setColumnsSizing = useSetRecoilState(tableColumnsSizingAF(entity));
  const {
    setTableSortingQsByValue,
    setTableGroupingQsByValue,
    setFilterSearchQsByValue,
  } = useCustomQueryParams();

  const onViewDropdownChange = useCallback(
    (newValue: unknown | undefined): void => {
      if (!newValue) {
        setActiveViewId(undefined);
        return;
      }

      const viewId = (newValue as IDropdownItem).value;
      const viewState = views?.find(
        view => String(view.id) === String(viewId),
      )?.viewState;

      if (!viewState) {
        Log.warnAndToast(t("views.errors.invalidIdSelected"));
        return;
      }

      setIsUpdating(true);
      setActiveViewId(Number(viewId));

      if (companyId) {
        updateFinalSchemaLocalStore({
          companyId,
          page: entity,
          finalSchema: viewState,
        });
      }

      Log.debug(`View chosen ${viewId}, setting view schema:`, viewState);

      if (viewState.filtersSchema) {
        Log.debug("Set filter schema from view ", viewState.filtersSchema);
        setFiltersSchema(viewState.filtersSchema);
        setFilterSearchQsByValue(viewState.filtersSchema);
        resetPageOnFiltersChange();
      }

      if (viewState.columnsVisibility && hasKeys(viewState.columnsVisibility)) {
        Log.debug(
          "Set column visibility from view ",
          viewState.columnsVisibility,
        );
        setColumnVisibility(viewState.columnsVisibility);
      }

      if (viewState.columnsSorting?.length) {
        Log.debug("Set column sorting from view ", viewState.columnsSorting);
        setColumnSorting(viewState.columnsSorting);
        setTableSortingQsByValue(viewState.columnsSorting);
      } else if (isArray(viewState.columnsSorting)) {
        // reset column sorting if column sorting from view is empty array, not undefined
        Log.debug("Reset column sorting from view ", viewState.columnsSorting);
        setColumnSorting([]);
        setTableSortingQsByValue(undefined);
      }

      if (viewState.columnsGrouping?.length) {
        Log.debug(
          "Reset column grouping from view ",
          viewState.columnsGrouping,
        );
        setColumnGrouping(viewState.columnsGrouping);
        setTableGroupingQsByValue(viewState.columnsGrouping);
      } else if (
        viewState.columnsGrouping &&
        isArray(viewState.columnsGrouping)
      ) {
        Log.debug(
          "Reset column NO grouping from view ",
          viewState.columnsGrouping,
        );
        setColumnGrouping([NO_GROUPING_QS_VALUE]);
        setTableGroupingQsByValue([NO_GROUPING_QS_VALUE]);
      }

      if (viewState.columnsOrder?.length) {
        Log.debug("Reset column order from view ", viewState.columnsOrder);
        setColumnOrder(viewState.columnsOrder);
      }

      // A view saved before widths were stored has none, and leaves the current widths as they are.
      if (viewState.columnsSizing) {
        Log.debug("Set column widths from view ", viewState.columnsSizing);
        setColumnsSizing(viewState.columnsSizing);
      }

      setIsUpdating(false);
      setIsOpen?.(false);
    },
    [
      companyId,
      entity,
      resetPageOnFiltersChange,
      setActiveViewId,
      setColumnGrouping,
      setColumnOrder,
      setColumnSorting,
      setColumnVisibility,
      setColumnsSizing,
      setFilterSearchQsByValue,
      setFiltersSchema,
      setIsOpen,
      setIsUpdating,
      setTableGroupingQsByValue,
      setTableSortingQsByValue,
      t,
      views,
    ],
  );

  const handleDeleteView = useCallback(
    async (viewId: number) => {
      if (String(activeViewId) === String(viewId)) {
        setActiveViewId(undefined);
      }

      setIsUpdating(true);
      await deleteViewHandler(viewId);
      setIsUpdating(false);
      setIsOpen?.(false);
    },
    [
      activeViewId,
      deleteViewHandler,
      setActiveViewId,
      setIsOpen,
      setIsUpdating,
    ],
  );

  const handleUpdateView = useCallback(
    async (viewId: number) => {
      setIsUpdating(true);
      await updateViewCriteriaOnlyHandler(viewId);
      setIsOpen?.(false);
      setIsUpdating(false);
      setActiveViewId(viewId);
    },
    [setActiveViewId, setIsOpen, setIsUpdating, updateViewCriteriaOnlyHandler],
  );
  const editViewHandler = useCallback(
    async (viewId: number) => {
      openEditView(views?.find(v => v.id === viewId));
    },
    [openEditView, views],
  );

  const handleToggle = useEvent(() => setIsOpen?.(prev => !prev));
  const handleClose = useEvent(() => setIsOpen?.(false));

  const mappedViews: IDropdownItem[] = useMemo(() => {
    if (!views?.length) {
      return [];
    }

    return views.map(view => ({ value: view.id || 0, label: view.name }));
  }, [views]);
  return {
    handleClose,
    handleDeleteView,
    handleToggle,
    handleUpdateView,
    onViewDropdownChange,
    editViewHandler,
    isOpen,
    mappedViews,
  };
};
